import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TailSpin } from 'react-loader-spinner';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Colors from '../../library/Colors';
import timeClockIcon from '../../assets/mobile-icons/ic_time_clock.png';
import { CorrectionDialog, DayDialog, DecisionDialog, ExportDialog, tcPost } from './TimeClockDialogs';
import TimeClockOverviewTab from './TimeClockOverviewTab';
import TimeClockHoursTab from './TimeClockHoursTab';
import TimeClockEmployeesTab from './TimeClockEmployeesTab';
import TimeClockSettingsTab from './TimeClockSettingsTab';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import { errorText, isoDate, mondayOf, surface, tone, TIMECLOCK_MIN_DB_VERSION } from './tcFormat';
import SftGlobalData from '../../library/SftGlobalData';

const PRESENT_REFRESH_MS = 60000;
const OVERVIEW_REFRESH_MS = 5 * 60000;
/** Back on the browser tab after this long: refresh the overview. */
const STALE_MS = 2 * 60000;

/**
 * TimeClockScreen — "Tijdsregistratie" (Prikklok 2027, phase 3).
 *
 * The HR side of the time clock: who is in, hours per day/week/month,
 * requests and incomplete days to handle, the payroll and inspection
 * exports, per-employee settings and the TIMECLOCK program variables.
 *
 * Everything goes through login.php?route=timeclock, which calls the v2
 * time clock endpoints as the POS user of this login; only time clock
 * managers get past the first call.
 */
const TimeClockScreen = (props) => {
    const t = props.t || ((k, d) => d || k);
    const lang = props.lang || 'nl';
    const api = props.api;

    const [tab, setTabState] = useState(() => {
        try { return parseInt(window.sessionStorage.getItem('tc_tab') || '0', 10) || 0; } catch (e) { return 0; }
    });
    const setTab = (value) => {
        try { window.sessionStorage.setItem('tc_tab', String(value)); } catch (e) { /* private mode */ }
        setTabState(value);
    };

    const [storeId, setStoreId] = useState('');
    const [weekFrom, setWeekFrom] = useState(mondayOf(isoDate(new Date())));
    const [overview, setOverview] = useState(null);
    const [gateError, setGateError] = useState('');
    // Client database version (null = not known yet): below 320 the time
    // clock tables do not exist, so the page is blocked.
    const [dbVersion, setDbVersion] = useState(null);
    useEffect(() => {
        if (!api) return;
        setDbVersion(null);
        SftGlobalData.post('sftData', { key: api, type: 'dbversion' }).then((response) => {
            const version = parseInt(response?.data?.data?.version, 10);
            setDbVersion(Number.isNaN(version) ? null : version);
        }).catch(() => setDbVersion(null));
    }, [api]);
    const [loadError, setLoadError] = useState('');
    // Reload triggers: the overview, and the other tabs. Kept apart so a
    // refresh never sends two requests at once to the API.
    const [overviewKey, setOverviewKey] = useState(0);
    const [tabKey, setTabKey] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const [lastLoaded, setLastLoaded] = useState(null);
    const overviewBusy = useRef(false);
    const overviewAgain = useRef(false);
    const overviewStale = useRef(false);

    const [correction, setCorrection] = useState(null);
    const [day, setDay] = useState(null);
    const [decision, setDecision] = useState(null);
    const [exportLayout, setExportLayout] = useState(null);
    const [exportMenu, setExportMenu] = useState(null);
    const [verify, setVerify] = useState(null);

    const containerClass = (props.screenWidth < 600 ? 'not_expanded ' : 'expanded ')
        + 'container_content _80perc ' + props.theme + ' container_content_' + props.theme;

    const notify = useCallback((text, ok) => {
        if (props.notify) props.notify(text, 5000, 'top-right', props.theme, ok ? 1 : 2);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.theme]);

    // --- data ----------------------------------------------------------------

    const loadOverview = useCallback(() => {
        if (!api) return;
        // One request at a time; a change during a load (other week or
        // store) loads again right after it.
        if (overviewBusy.current) { overviewAgain.current = true; return; }
        overviewBusy.current = true;
        setRefreshing(true);
        tcPost('overview', api, { week_from: weekFrom, store_id: storeId }).then((response) => {
            overviewBusy.current = false;
            setRefreshing(false);
            if (overviewAgain.current) {
                overviewAgain.current = false;
                setOverviewKey((k) => k + 1);
                return;
            }
            if (response?.success) {
                setOverview(response.data);
                setGateError('');
                setLoadError('');
                setLastLoaded(new Date());
                return;
            }
            const key = response?.translation_key;
            // Not a manager, no update, no API: nothing else on this page works.
            if (['timeclock_not_manager', 'timeclock_db_update', 'timeclock_no_api', 'timeclock_api_missing'].includes(key)) {
                setGateError(errorText(t, response));
            } else {
                setLoadError(errorText(t, response));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, weekFrom, storeId]);

    useEffect(() => { loadOverview(); }, [loadOverview, overviewKey]);

    // Who is in: a light refresh every minute while the overview is open.
    const overviewRef = useRef(overview);
    overviewRef.current = overview;
    useEffect(() => {
        if (tab !== 0 || !api) return undefined;
        const timer = setInterval(() => {
            if (document.hidden || !overviewRef.current || overviewBusy.current) return;
            tcPost('present', api, { store_id: storeId }).then((response) => {
                if (response?.success && overviewRef.current) {
                    setOverview({ ...overviewRef.current, present: response.data.present });
                }
            });
        }, PRESENT_REFRESH_MS);
        return () => clearInterval(timer);
    }, [tab, api, storeId]);

    // Refresh what is on screen; the overview catches up when it is shown again.
    const tabRef = useRef(tab);
    tabRef.current = tab;
    const refresh = useCallback(() => {
        if (tabRef.current === 0) {
            setOverviewKey((k) => k + 1);
        } else {
            overviewStale.current = true;
            setTabKey((k) => k + 1);
        }
    }, []);

    useEffect(() => {
        if (tab === 0 && overviewStale.current) {
            overviewStale.current = false;
            setOverviewKey((k) => k + 1);
        }
    }, [tab]);

    // The whole overview every 5 minutes while it is visible, and when the
    // browser tab comes back after a while. Not while a dialog is open.
    const dialogOpen = useRef(false);
    dialogOpen.current = !!(day || correction || decision || exportLayout);
    const lastLoadedRef = useRef(lastLoaded);
    lastLoadedRef.current = lastLoaded;
    useEffect(() => {
        if (tab !== 0 || !api) return undefined;
        const timer = setInterval(() => {
            if (!document.hidden && !dialogOpen.current) setOverviewKey((k) => k + 1);
        }, OVERVIEW_REFRESH_MS);
        const onVisible = () => {
            const last = lastLoadedRef.current;
            if (!document.hidden && !dialogOpen.current && (!last || Date.now() - last.getTime() > STALE_MS)) {
                setOverviewKey((k) => k + 1);
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [tab, api]);

    const tabLoaded = useCallback(() => setLastLoaded(new Date()), []);

    const storeName = useCallback((code) => {
        if (!code) return '';
        const store = (overview?.stores || []).find((s) => s.code === code);
        return store ? store.oms : t('tc_store', 'Store') + ' ' + code;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overview]);

    const runVerify = () => {
        setVerify({ busy: true });
        tcPost('verify', api).then((response) => {
            if (response?.success) {
                const data = response.data || {};
                const checked = (data.events?.checked || 0) + (data.decisions?.checked || 0);
                setVerify({ ok: !!data.ok, checked, at: data.checked_at });
                notify(data.ok ? t('tc_chain_ok', 'Hash chain checked: OK') : t('tc_chain_broken', 'Hash chain check found a problem'), !!data.ok);
            } else {
                setVerify(null);
                notify(errorText(t, response), false);
            }
        });
    };

    // --- gates ---------------------------------------------------------------

    const s = surface(props.theme);
    const control = { textTransform: 'none', height: 40, px: 2, whiteSpace: 'nowrap' };
    const verifyColor = verify && !verify.busy ? (verify.ok ? tone.ok : tone.bad) : undefined;

    const header = (
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2, rowGap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 'auto', minWidth: 0 }}>
                <Box component="img" src={timeClockIcon} alt="" sx={{ width: 40, height: 40, borderRadius: '8px', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography component="h1" sx={{ fontSize: 21, fontWeight: 700, lineHeight: 1.25 }}>{t('tc_page_title', 'Time registration')}</Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.4 }}>
                        {overview?.me?.name
                            ? t('tc_signed_in_as', 'you act as') + ' ' + overview.me.name + ' (' + overview.me.id + ')'
                            : t('tc_page_subtitle', 'Who is in, hours, corrections and exports')}
                    </Typography>
                </Box>
            </Box>
            {overview && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Tooltip title={t('tc_refresh', 'Refresh')}>
                        <span>
                            <Button variant="text" onClick={refresh} disabled={refreshing}
                                startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                                sx={{ ...control, px: 1.25, color: 'text.secondary', fontWeight: 400 }}>
                                {lastLoaded
                                    ? t('tc_updated_at', 'Updated') + ' ' + String(lastLoaded.getHours()).padStart(2, '0') + ':' + String(lastLoaded.getMinutes()).padStart(2, '0')
                                    : t('tc_refresh', 'Refresh')}
                            </Button>
                        </span>
                    </Tooltip>
                    <TextField select size="small" label={t('tc_store', 'Store')} value={storeId} onChange={(e) => setStoreId(e.target.value)}
                        sx={{ width: 220, '& .MuiInputBase-root': { height: 40 } }}
                        slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}>
                        <MenuItem value="">{t('tc_all_stores', 'All stores')}</MenuItem>
                        {(overview.stores || []).map((store) => (
                            <MenuItem key={store.code} value={store.code}>{store.code} — {store.oms}</MenuItem>
                        ))}
                    </TextField>
                    <Tooltip title={verify?.at ? t('tc_chain_checked_at', 'Last check') + ' ' + verify.at.substring(11, 16) + ' · ' + verify.checked + ' ' + t('tc_records', 'records') : t('tc_chain_hint', 'Checks that no stamp or decision was changed or removed afterwards.')}>
                        <span>
                            <Button variant="outlined" disabled={verify?.busy} onClick={runVerify}
                                sx={{ ...control, color: verifyColor, borderColor: verifyColor }}>
                                {verify?.busy ? t('tc_busy', 'One moment…')
                                    : verify ? (verify.ok ? '✓ ' + t('tc_chain_ok_short', 'Chain OK') : '✕ ' + t('tc_chain_broken_short', 'Chain broken'))
                                        : t('tc_chain_check', 'Check hash chain')}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button variant="contained" disableElevation sx={control} endIcon={<KeyboardArrowDown />}
                        onClick={(e) => setExportMenu(e.currentTarget)}>
                        {t('tc_export', 'Export')}
                    </Button>
                    <Menu anchorEl={exportMenu} open={!!exportMenu} onClose={() => setExportMenu(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                        <MenuItem onClick={() => { setExportMenu(null); setExportLayout('payroll'); }}>{t('tc_export_payroll', 'Payroll office')}</MenuItem>
                        <MenuItem onClick={() => { setExportMenu(null); setExportLayout('inspection'); }}>{t('tc_export_inspection', 'Inspection')}</MenuItem>
                    </Menu>
                </Box>
            )}
        </Box>
    );

    if (dbVersion !== null && dbVersion < TIMECLOCK_MIN_DB_VERSION) {
        return (
            <div className={containerClass}>
                {header}
                <Box sx={{ ...s.card, p: 3, maxWidth: 640 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>{t('tc_db_update_title', 'Database update required')}</Typography>
                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                        {t('tc_db_update_text', 'The time registration needs a newer database version.')}
                        {' (' + t('tc_db_current', 'current') + ': ' + dbVersion + ', ' + t('tc_db_required', 'required') + ': ' + TIMECLOCK_MIN_DB_VERSION + ')'}
                    </Typography>
                </Box>
            </div>
        );
    }

    if (gateError) {
        return (
            <div className={containerClass}>
                {header}
                <Box sx={{ ...s.card, p: 3, maxWidth: 640 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>{t('tc_no_access_title', 'This page is not available')}</Typography>
                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{gateError}</Typography>
                </Box>
            </div>
        );
    }

    if (!overview) {
        return (
            <div className={containerClass}>
                {header}
                {loadError ? (
                    <Box sx={{ py: 4 }}>
                        <Typography sx={{ color: tone.bad, fontSize: 14, mb: 1.5 }}>{loadError}</Typography>
                        <Button variant="outlined" size="small" onClick={() => setOverviewKey((k) => k + 1)} sx={{ textTransform: 'none' }}>{t('tc_retry', 'Try again')}</Button>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <TailSpin height="40" width="40" color={Colors.sft_blue} ariaLabel="loading" />
                    </Box>
                )}
            </div>
        );
    }

    // --- screen --------------------------------------------------------------

    // Waiting requests and incomplete days: shown on the Overview tab.
    const attention = (overview.pending || []).length + (overview.incomplete || []).length;

    const afterChange = (message) => {
        if (message) notify(message, true);
        refresh();
    };

    return (
        <div className={containerClass}>
            {header}
            <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
                sx={{ mb: 2.5, minHeight: 44, borderBottom: s.line, '& .MuiTab-root': { textTransform: 'none', minHeight: 44, fontSize: 14.5, fontWeight: 600, px: 2 } }}>
                <Tab label={attention > 0
                    ? <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        {t('tc_tab_overview', 'Overview')}
                        <Box component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: '#fff', backgroundColor: tone.bad, borderRadius: '10px', px: 0.8, lineHeight: '18px' }}>{attention}</Box>
                    </Box>
                    : t('tc_tab_overview', 'Overview')} />
                <Tab label={t('tc_tab_hours', 'Hours')} />
                <Tab label={t('tc_tab_employees', 'Employees')} />
                <Tab label={t('tc_tab_settings', 'Settings')} />
            </Tabs>

            {loadError && <Typography sx={{ color: tone.bad, fontSize: 13.5, mb: 1 }}>{loadError}</Typography>}

            {tab === 0 && (
                <TimeClockOverviewTab data={overview} t={t} lang={lang} theme={props.theme} storeName={storeName}
                    weekFrom={weekFrom} setWeekFrom={setWeekFrom}
                    onDay={setDay} onCorrect={setCorrection}
                    onDecide={(request, approve) => setDecision({ request, approve })} />
            )}
            {tab === 1 && (
                <TimeClockHoursTab t={t} lang={lang} theme={props.theme} api={api} storeId={storeId} storeName={storeName}
                    reloadKey={tabKey} onLoaded={tabLoaded} onDay={setDay} />
            )}
            {tab === 2 && (
                <TimeClockEmployeesTab t={t} lang={lang} theme={props.theme} api={api} storeName={storeName} notify={notify} reloadKey={tabKey} onLoaded={tabLoaded} />
            )}
            {tab === 3 && (
                <TimeClockSettingsTab t={t} theme={props.theme} api={api} notify={notify} reloadKey={tabKey} onLoaded={tabLoaded} />
            )}

            <DayDialog open={!!day && !correction} day={day} t={t} lang={lang} api={api} theme={props.theme}
                onClose={() => setDay(null)}
                onCorrect={(preset) => setCorrection(preset)} />
            <CorrectionDialog open={!!correction} preset={correction} t={t} lang={lang} api={api} employees={overview.employees}
                onClose={() => setCorrection(null)}
                onDone={() => {
                    setCorrection(null);
                    setDay(null);
                    afterChange(t('tc_correction_done', 'Correction saved.'));
                }} />
            <DecisionDialog open={!!decision} request={decision?.request} approve={decision?.approve} t={t} lang={lang} api={api}
                onClose={() => setDecision(null)}
                onDone={() => {
                    const approved = decision?.approve;
                    setDecision(null);
                    afterChange(approved ? t('tc_approved', 'Approved') : t('tc_rejected_done', 'Rejected'));
                }} />
            <ExportDialog open={!!exportLayout} initialLayout={exportLayout} storeId={storeId} t={t} api={api} notify={notify}
                onClose={() => setExportLayout(null)} />
        </div>
    );
};

export default TimeClockScreen;
